import { useEffect, useState } from "react";

import { Loader } from "lucide-react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { getPerceptionTrend } from "../api/perception";

// Preferred display labels for the known taxonomy, mirroring
// src/config/perception_attributes.json on the backend. This is only a
// presentation nicety — any attribute id the backend returns is still shown
// (via labelFor's humanized fallback), so the two drifting can't hide data.
const ATTRIBUTE_LABELS = {
  location: "Location",
  service: "Service",
  room_quality: "Room Quality",
  cleanliness: "Cleanliness",
  value_for_money: "Value for Money",
  dining: "Food & Dining",
  amenities: "Amenities",
  uniqueness: "Uniqueness / Character",
  family_friendliness: "Family-Friendliness",
  romantic_appeal: "Romantic Appeal",
  business_facilities: "Business Facilities",
  accessibility: "Accessibility",
  sustainability: "Sustainability",
};

const COMPOSITE = "composite";

// Known ids get their curated label; anything else the backend sends is
// humanized (snake_case → Title Case) so it still appears in the selector.
const labelFor = (id) =>
  ATTRIBUTE_LABELS[id] ??
  id
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
};

const sharedAxisProps = {
  tick: { fill: "var(--color-on-surface-variant, #9aa0a6)", fontSize: 11 },
  tickLine: false,
  axisLine: false,
};
const sharedTooltipProps = {
  contentStyle: {
    background: "var(--color-surface-container-lowest, #1a1a2e)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 12,
    fontSize: 12,
  },
  labelStyle: { color: "var(--color-on-surface-variant, #9aa0a6)" },
};

const PerceptionTrends = () => {
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(COMPOSITE);

  useEffect(() => {
    getPerceptionTrend()
      .then((r) => setTrend(r.data))
      .catch(() => setTrend(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 gap-3">
        <Loader className="w-6 h-6 animate-spin text-primary" />
        <span className="text-on-surface-variant text-sm">Loading trends…</span>
      </div>
    );
  }

  const series = trend?.series ?? [];

  if (!series.length) {
    return (
      <div className="bg-surface-container-low rounded-2xl p-10 border border-surface-container-high text-center text-on-surface-variant text-sm">
        No trend data yet — generate reports over time to see trends.
      </div>
    );
  }

  // Every attribute present in at least one point: known taxonomy first (in
  // taxonomy order), then any unknown ids the backend returned, so nothing the
  // API sends is dropped from the selector.
  const presentInSeries = (id) => series.some((pt) => pt.attribute_scores?.[id] !== undefined);
  const allPresentIds = new Set(series.flatMap((pt) => Object.keys(pt.attribute_scores ?? {})));
  const knownIds = Object.keys(ATTRIBUTE_LABELS).filter(presentInSeries);
  const unknownIds = [...allPresentIds].filter((id) => !(id in ATTRIBUTE_LABELS));
  const presentAttributeIds = [...knownIds, ...unknownIds];

  const isComposite = selected === COMPOSITE;
  const maxScore = isComposite ? 100 : 10;
  const chartData = series.map((pt) => ({
    date: formatDate(pt.date),
    score: isComposite ? pt.composite_score : (pt.attribute_scores?.[selected] ?? null),
  }));

  return (
    <div className="space-y-6">
      <h2 className="font-headline text-xl font-bold">Score Trends</h2>

      {/* Selector */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setSelected(COMPOSITE)}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors border ${
            isComposite
              ? "bg-primary/10 border-primary text-primary"
              : "bg-surface-container-lowest border-outline-variant/30 text-on-surface-variant hover:border-primary/50"
          }`}
        >
          Composite Score
        </button>
        {presentAttributeIds.map((id) => (
          <button
            key={id}
            onClick={() => setSelected(id)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors border ${
              selected === id
                ? "bg-primary/10 border-primary text-primary"
                : "bg-surface-container-lowest border-outline-variant/30 text-on-surface-variant hover:border-primary/50"
            }`}
          >
            {labelFor(id)}
          </button>
        ))}
      </div>

      {/* Chart */}
      <div className="bg-surface-container-low rounded-2xl p-6 border border-surface-container-high">
        <h3 className="font-headline text-base font-bold mb-4">
          {isComposite ? "Composite Score Over Time" : `${labelFor(selected)} Over Time`}
        </h3>

        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis dataKey="date" {...sharedAxisProps} />
            <YAxis domain={[0, maxScore]} {...sharedAxisProps} width={32} />
            <Tooltip {...sharedTooltipProps} />
            <Line
              type="monotone"
              dataKey="score"
              name={isComposite ? "Composite Score" : labelFor(selected)}
              stroke="#2E5BFF"
              strokeWidth={2}
              dot={{ r: 4, fill: "#2E5BFF" }}
              activeDot={{ r: 6 }}
              connectNulls
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default PerceptionTrends;
